import {useQuery} from "@tanstack/react-query";
import {useCallback, useMemo} from "react";
import {adapterBankWithAccount, findBankData} from "../../adapters/bankListAdapter";
import {getAccountPagoMovil} from "../../services/wallet/accounts";
import {useAuthStore} from "../../store/useAuth.store";

interface PagoMovilAccount {
  ID: number;
  phone_number: string;
  bank_code: number;
}

interface PagoMovilApiResponse {
  data: {data: PagoMovilAccount[]};
  status: number;
}

export interface BankAccount {
  id: number;
  phone: string;
  bankCode: number;
  bankName: string;
}

const useGetAccountPagoMovil = () => {
  const authStatus = useAuthStore(state => state.authStatus);
  const {data, isError, isPending, isSuccess, refetch, error} = useQuery<
    PagoMovilApiResponse,
    Error,
    PagoMovilAccount[]
  >({
    queryKey: ["userMobileAccount"],
    queryFn: getAccountPagoMovil as () => Promise<PagoMovilApiResponse>,
    staleTime: 5 * 60 * 1000,
    select: response => response?.data?.data ?? [],
    enabled: authStatus === "authenticated",
  });

  const accounts = data ?? [];

  const options = useMemo(() => adapterBankWithAccount(accounts), [accounts]);

  const getAccountById = useCallback(
    (id: number): BankAccount | undefined => {
      const account = accounts.find(item => item.ID === id);
      if (!account) return undefined;
      const bankData = findBankData(account);
      return {
        id: account.ID,
        phone: account.phone_number,
        bankCode: account.bank_code,
        bankName: bankData?.bank_name ?? String(account.bank_code),
      };
    },
    [accounts]
  );

  return {
    data: accounts,
    options,
    getAccountById,
    isError,
    isPending,
    isLoading: isPending,
    isSuccess,
    refetch,
    error,
  };
};

export default useGetAccountPagoMovil;
